import { useMemo, useReducer } from "react";
import PropTypes from "prop-types";
import { settings } from "../../../shim/jsbridge";
import { extractWarnings } from "../utils";

// UTILS
const errorRegex = /(?<line>\d+:)/;

const extractEditorError = (errors = []) => {
  if (errors.length > 1) return null;
  return errors.map(error => {
    const { line = 1 } = error.match(errorRegex)?.groups || {};
    return { msg: error, line: parseInt(line, 10) };
  })[0];
};

// ACTIONS
const ActionEnum = Object.freeze({
  SAVE_ERROR: "save_error",
  RULE_SAVED: "rule_saved",
  RULESET_ENABLE: "ruleset_enable",
  CLOUD_ENABLE: "cloud_enable",
  RULESET_NAME_CHANGED: "ruleset_name_changed",
  RULE_SELECTED: "rule_selected",
  RULE_FETCHED: "rule_fetched",
  EDITOR_VALUE_CHANGED: "editor_value_changed"
});

// STATE
const yaraModalInitialState = {
  initEditorValue: "",
  initEnableRuleset: true,
  enableRuleset: true,
  initEnableRulesetTiCloud: settings.YARA_ENABLE_AUTO_UPLOAD,
  initAutoCloudRetro: settings.YARA_AUTO_CLOUD_RETRO,
  enableRulesetTiCloud: settings.YARA_ENABLE_AUTO_UPLOAD,
  rulesetName: "",
  rulesetNameError: false,
  needsSave: false,
  isEditorEmpty: true,
  ruleId: null, // currently selected ruleId
  activeRuleId: null, // active ruleId => eg ruleId which is on top of history
  ruleSaved: false,
  savesPassed: 0, // used to retrigger fetch on save when ruleId stays the same => enabling/disabling rule
  warnings: [],
  editorError: null
};
const useYaraEditorReducer = ruleId => {
  const [state, dispatch] = useReducer(
    (prevState, { type, payload }) => {
      switch (type) {
        case ActionEnum.SAVE_ERROR:
          return {
            ...prevState,
            ruleSaved: false,
            editorError: extractEditorError(payload)
          };
        case ActionEnum.RULE_SAVED:
          return {
            ...prevState,
            ruleSaved: true,
            savesPassed: prevState.savesPassed + 1,
            ruleId: payload.details.id,
            activeRuleId: payload.details.id, // on save, we load the new saved rule as currently active
            needsSave: false
          };
        case ActionEnum.RULESET_ENABLE:
          return {
            ...prevState,
            enableRuleset: payload,
            needsSave: prevState.initEnableRuleset !== payload
          };
        case ActionEnum.CLOUD_ENABLE:
          return {
            ...prevState,
            enableRulesetTiCloud: payload,
            needsSave: prevState.initEnableRulesetTiCloud !== payload
          };
        case ActionEnum.RULESET_NAME_CHANGED:
          return {
            ...prevState,
            rulesetName: payload.value,
            rulesetNameError: payload.hasError
          };
        case ActionEnum.RULE_SELECTED:
          return {
            ...prevState,
            ruleId: payload,
            needsSave: payload !== prevState.activeRuleId
          };
        case ActionEnum.RULE_FETCHED:
          return {
            ...prevState,
            isEditorEmpty: false,
            initEditorValue: payload.details.content,
            initEnableRuleset: !payload.details.is_disabled,
            initEnableRulesetTiCloud: payload.details.saved_in_cloud,
            enableRuleset: !payload.details.is_disabled,
            enableRulesetTiCloud: payload.details.saved_in_cloud,
            editorError: extractEditorError(payload.errors),
            warnings: extractWarnings(payload?.warnings),
            rulesetName: payload.details.name,
            activeRuleId: !prevState.activeRuleId
              ? ruleId
              : prevState.activeRuleId // setActiveRule on initial fetch only
          };
        case ActionEnum.EDITOR_VALUE_CHANGED:
          return {
            ...prevState,
            isEditorEmpty: payload.length < 1,
            needsSave: prevState.initEditorValue !== payload
          };
        default:
          throw new Error("Invalid action type");
      }
    },
    yaraModalInitialState,
    () => ({ ...yaraModalInitialState, ruleId })
  );

  const actions = useMemo(
    () => ({
      saveError: errors =>
        dispatch({
          type: ActionEnum.SAVE_ERROR,
          payload: errors
        }),
      ruleSaved: respData =>
        dispatch({
          type: ActionEnum.RULE_SAVED,
          payload: respData
        }),
      rulesetEnable: value =>
        dispatch({
          type: ActionEnum.RULESET_ENABLE,
          payload: value
        }),
      rulesetCloudEnable: value =>
        dispatch({
          type: ActionEnum.CLOUD_ENABLE,
          payload: value
        }),
      rulesetNameChanged: (value, hasError) =>
        dispatch({
          type: ActionEnum.RULESET_NAME_CHANGED,
          payload: { value, hasError }
        }),
      ruleSelected: ruleID =>
        dispatch({
          type: ActionEnum.RULE_SELECTED,
          payload: ruleID
        }),
      ruleFetch: respData =>
        dispatch({
          type: ActionEnum.RULE_FETCHED,
          payload: respData
        }),
      editorValueChanged: value =>
        dispatch({
          type: ActionEnum.EDITOR_VALUE_CHANGED,
          payload: value
        })
    }),
    [dispatch]
  );
  return [state, actions];
};

useYaraEditorReducer.PropTypes = {
  id: PropTypes.number
};

export default useYaraEditorReducer;
